import React, { useState } from 'react';
import {createRoot} from 'react-dom/client';
// Compile via @tailwindcss/vite (do not import prebuilt output.css — Vite would
// reprocess it and strip utilities that the content scanner misses).
import './assets/styles/input.css';
import {ThemeProvider} from "./contexts/ThemeContext";
import {QuestionProvider} from "./contexts/QuestionContext";
import {QueryClientProvider} from '@tanstack/react-query';
import {queryClient} from './data/queryClient';
import {BrowserRouter, Link, Route, Routes, useLocation} from 'react-router-dom';
import {AuthProvider, useAuth} from './contexts/AuthContext';
import {SubscriptionProvider} from './contexts/SubscriptionContext';
import ProtectedRoute from './components/ProtectedRoute';
import Login from './pages/LoginPage';
import Register from './pages/RegisterPage';
import LandingPage from './pages/LandingPage';
import Footer from './components/Footer';

const CloudPrepApp = React.lazy(() => import('./App'));
const AdminPage = React.lazy(() => import('./components/pages/AdminPage'));
const TryQuizPage = React.lazy(() => import('./pages/TryQuizPage'));
const ProgressDashboardPage = React.lazy(() => import('./pages/ProgressDashboardPage'));
const BlogListPage = React.lazy(() => import('./pages/BlogListPage'));
const BlogPostPage = React.lazy(() => import('./pages/BlogPostPage'));
const NotFoundPage = React.lazy(() => import('./pages/NotFoundPage'));
const SubscriptionSuccess = React.lazy(() => import('./components/Subscription/SubscriptionSuccess'));
const ExtractImageText = React.lazy(() => import('./components/Admin/ExtractImageText'));

const RouteFallback = () => <div className="page-below-nav landing-rainbow" aria-busy="true"/>;
import { ThemeSelector } from './contexts/ThemeContext';
import { installConsoleRedirect } from './utils/logger';

installConsoleRedirect();

const ReactQueryDevtoolsLazy = React.lazy(() =>
    import('@tanstack/react-query-devtools').then((mod) => ({
        default: () => <mod.ReactQueryDevtools initialIsOpen={false} />,
    }))
);
  
// Navigation component (needs to be inside AuthProvider to use useAuth)
const Navigation = () => {
    const { user, isAuthenticated, isAdmin, logout } = useAuth();
    const [showOcrModal, setShowOcrModal] = React.useState(false);
    const location = useLocation();

    // Determine if we're on the quiz page (main route)
    const isOnQuizPage = location.pathname === '/';

    const navRef = React.useRef<HTMLElement>(null);
    React.useEffect(() => {
        const nav = navRef.current;
        if (!nav) return;
        const setNavHeight = () =>
            document.documentElement.style.setProperty('--nav-h', `${nav.offsetHeight}px`);
        setNavHeight();
        const observer = new ResizeObserver(setNavHeight);
        observer.observe(nav);
        return () => observer.disconnect();
    }, []);

    return (
        <>
            <nav ref={navRef} className="nav-rainbow">
                <div className="mx-auto px-3 sm:px-4 py-3 sm:py-4 grid grid-cols-1 sm:grid-cols-[1fr_auto_1fr] gap-3 sm:gap-4 items-center">
                    <div className="flex flex-wrap gap-4 sm:gap-6 items-center sm:col-start-1 sm:row-start-1 order-2 sm:order-none">
                        <Link
                            to="/"
                            className="font-medium transition-colors text-sm sm:text-base"
                        >
                            Quiz
                        </Link>

                        {isAuthenticated && (
                            <Link
                                to="/dashboard"
                                className="font-medium transition-colors text-sm sm:text-base"
                            >
                                Dashboard
                            </Link>
                        )}

                        <Link
                            to="/blog"
                            className="font-medium transition-colors text-sm sm:text-base"
                        >
                            Blog
                        </Link>
                        
                        {/* Admin Tools - Only show when on quiz page AND user is admin */}
                        {isAdmin && isOnQuizPage && (
                            <div className="flex flex-wrap gap-2 sm:gap-3 sm:ml-4 sm:pl-4 sm:border-l" style={{ borderColor: 'var(--border)' }}>
                                <button
                                    onClick={() => {
                                        // Trigger admin section in CloudPrepApp
                                        window.dispatchEvent(new CustomEvent('navigateToAdmin'));
                                    }}
                                    className="nav-admin-tool nav-admin-tool-purple"
                                >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                    </svg>
                                    Admin Panel
                                </button>
                                <button
                                    onClick={() => setShowOcrModal(true)}
                                    className="nav-admin-tool nav-admin-tool-green"
                                >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                    </svg>
                                    OCR Tool
                                </button>
                            </div>
                        )}
                    </div>

                    <Link
                        to="/"
                        className="text-2xl sm:text-3xl font-bold text-center sm:col-start-2 sm:row-start-1 order-1 sm:order-none tracking-tight"
                        style={{ color: 'var(--text)' }}
                    >
                        Cloud Prepper
                    </Link>

                    <div className="flex flex-wrap items-center justify-center sm:justify-end gap-2 sm:gap-4 sm:col-start-3 sm:row-start-1 order-3 sm:order-none w-full sm:w-auto">
                        <ThemeSelector />
                        {isAuthenticated ? (
                            <>
                                <span className="nav-muted text-sm hidden md:inline max-w-[10rem] truncate">
                                    Welcome, <span className="font-semibold" style={{ color: 'var(--text)' }}>{user?.username}</span>
                                    {isAdmin && <span className="ml-2 px-2 py-0.5 bg-blue-600 rounded text-xs text-white">Admin</span>}
                                </span>
                                <button
                                    onClick={logout}
                                    className="nav-btn-outline text-sm font-medium transition-colors px-3 py-1 rounded border"
                                >
                                    Logout
                                </button>
                            </>
                        ) : (
                            <>
                                <Link
                                    to="/login"
                                    className="nav-cta bg-green-600 hover:bg-green-700 text-white text-sm font-medium px-3 py-1 rounded"
                                >
                                    Login
                                </Link>
                                <Link
                                    to="/register"
                                    className="nav-cta bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-3 py-1 rounded"
                                >
                                    Register
                                </Link>
                            </>
                        )}
                    </div>
                </div>
            </nav>
            
            {/* OCR Modal */}
            {showOcrModal && (
                <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex justify-center items-center p-4">
                    <div className="relative bg-white dark:bg-dark-900 rounded-xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden">
                        <div className="sticky top-0 bg-gradient-to-r from-slate-900 to-purple-900 px-6 py-4 flex justify-between items-center border-b border-white/10">
                            <h2 className="text-xl font-bold text-white">OCR Text Extraction</h2>
                            <button
                                onClick={() => setShowOcrModal(false)}
                                className="text-white/80 hover:text-white transition-colors"
                            >
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                        <div className="p-6 overflow-y-auto max-h-[calc(90vh-80px)]">
                            <React.Suspense fallback={<p className="p-6 text-center">Loading…</p>}>
                                <ExtractImageText />
                            </React.Suspense>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

// @ts-ignore
const root = createRoot(document.getElementById('root'));
root.render(
    <React.StrictMode>
        <QueryClientProvider client={queryClient}>
            <AuthProvider>
                <SubscriptionProvider>
                <ThemeProvider>
                    <QuestionProvider>
                        <BrowserRouter>
                            <div className="min-h-screen flex flex-col">
                                <Navigation />

                                {/* Routes */}
                                <React.Suspense fallback={<RouteFallback/>}>
                                <Routes>
                                    <Route
                                        path="/"
                                        element={
                                            <ProtectedRoute
                                                unauthenticatedRedirect="/welcome"
                                            >
                                                <CloudPrepApp/>
                                            </ProtectedRoute>
                                        }
                                    />
                                    <Route path="/welcome" element={<LandingPage />}/>
                                    <Route path="/try" element={<TryQuizPage />}/>
                                    <Route
                                        path="/dashboard"
                                        element={
                                            <ProtectedRoute>
                                                <ProgressDashboardPage/>
                                            </ProtectedRoute>
                                        }
                                    />
                                    <Route path="/login" element={<Login />}/>
                                    <Route path="/register" element={<Register />}/>
                                    <Route path="/blog" element={<BlogListPage />}/>
                                    <Route path="/blog/:slug" element={<BlogPostPage />}/>
                                    <Route path="/subscription/success" element={<SubscriptionSuccess />}/>
                                    <Route 
                                        path="/admin" 
                                        element={
                                            <ProtectedRoute requireAdmin={true}>
                                                <AdminPage/>
                                            </ProtectedRoute>
                                        }
                                    />
                                    <Route path="*" element={<NotFoundPage />}/>
                                </Routes>
                                </React.Suspense>

                                <Footer />
                            </div>
                        </BrowserRouter>
                    </QuestionProvider>
                </ThemeProvider>
                </SubscriptionProvider>
                {import.meta.env.DEV && (
                    <React.Suspense fallback={null}>
                        <ReactQueryDevtoolsLazy />
                    </React.Suspense>
                )}
            </AuthProvider>
        </QueryClientProvider>
    </React.StrictMode>
)
